import { useState, useRef, useEffect, useMemo } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  placeholder?: string;
}

export default function MultiSearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Поиск...',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const close = () => {
      setOpen(false);
      setQuery('');
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const handleContextMenu = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const handleToggle = () => {
    setOpen(prev => !prev);
    if (!open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleToggleOption = (val: string) => {
    if (selectedSet.has(val)) {
      onChange(value.filter(v => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const handleRemove = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== val));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedLabels = useMemo(
    () => value.map(v => options.find(o => o.value === v)).filter(Boolean) as Option[],
    [value, options]
  );

  return (
    <div className="ss-root" ref={rootRef}>
      {label && <label className="form-label">{label}</label>}
      <div className="ss-control" onClick={handleToggle} style={{ flexWrap: 'wrap', gap: 4, minHeight: 40 }}>
        {selectedLabels.length === 0 && (
          <span className="ss-placeholder" style={{ flex: 1 }}>— Не выбрано —</span>
        )}
        {selectedLabels.map(o => (
          <span key={o.value} className="mss-tag">
            {o.label}
            <button className="mss-tag-remove" onClick={(e) => handleRemove(o.value, e)} type="button">&times;</button>
          </span>
        ))}
        <span className={`ss-arrow ${open ? 'ss-arrow-open' : ''}`} style={{ marginLeft: 'auto' }}>&#9662;</span>
      </div>
      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-wrap">
            <input
              ref={inputRef}
              className="ss-search"
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQuery('');
                }
              }}
            />
            {query && (
              <button className="ss-search-clear" onClick={() => setQuery('')} type="button">&times;</button>
            )}
            <button
              className="mss-dropdown-close"
              onClick={() => { setOpen(false); setQuery(''); }}
              type="button"
              title="Закрыть"
            >&times;</button>
          </div>
          <div className="ss-options">
            {filtered.length === 0 && (
              <div className="ss-no-results">Ничего не найдено</div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                className={`ss-option ${selectedSet.has(o.value) ? 'ss-option-selected' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleToggleOption(o.value); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(o.value)}
                  readOnly
                  className="mss-checkbox"
                />
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {value.length > 0 && (
        <button className="ss-clear" onClick={handleClear} type="button" tabIndex={-1}>&times;</button>
      )}
    </div>
  );
}
